import { useCallback, useEffect, useRef, useState } from "react";
import { MonoLabel } from "../components/MonoLabel";
import { PTTButton } from "../components/PTTButton";
import { ToastStack, useToasts } from "../components/Toast";
import {
  RTCSessionManager,
  type TsuyakuState,
} from "../channels/tsuyaku/RTCSessionManager";
import type { RealtimeServerEvent } from "../channels/tsuyaku/events";
import {
  TurnHistory,
  type TsuyakuTurn,
} from "../channels/tsuyaku/TurnHistory";
import {
  TSUYAKU_LANGUAGE_LABELS,
  type Settings,
  type TsuyakuOutputLanguage,
} from "../lib/api-types";
import { applySinkId } from "../lib/devices";
import type { SelectedDevices } from "../lib/deviceStorage";

const MAX_TURNS_DISPLAYED = 10;

/**
 * Koe Tsuyaku tab — translation channel.
 * spec §1.2, §2.3.1.
 *
 * Target language comes from Settings (`tsuyakuOutputLanguage`). The source
 * language is auto-detected by `gpt-realtime-translate` (70+ supported
 * inputs), so there is no source-language selector.
 */
export function TsuyakuTab({
  settings,
  devices,
}: {
  settings: Settings;
  devices?: SelectedDevices;
}) {
  const [state, setState] = useState<TsuyakuState>("idle");
  const [turns, setTurns] = useState<TsuyakuTurn[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const managerRef = useRef<RTCSessionManager | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);

  // ── Per-turn bookkeeping ──────────────────────────────────
  // Latency: mark on PTT release, resolve on first output_audio_transcript.delta.
  const currentTurnIdRef = useRef<string | null>(null);
  const releaseAtRef = useRef<number | null>(null);
  const targetRef = useRef<TsuyakuOutputLanguage>(
    settings.tsuyakuOutputLanguage,
  );
  useEffect(() => {
    targetRef.current = settings.tsuyakuOutputLanguage;
    // Push the change live to an active session — RTCSessionManager
    // handles the case where the DataChannel is not open yet.
    managerRef.current?.setOutputLanguage(settings.tsuyakuOutputLanguage);
  }, [settings.tsuyakuOutputLanguage]);

  const { toasts, push: pushToast, dismiss: dismissToast } = useToasts();

  // ── Event handling ────────────────────────────────────────
  const handleEvent = useCallback((event: RealtimeServerEvent) => {
    switch (event.type) {
      case "conversation.item.input_audio_transcription.delta": {
        const delta = (event as { delta?: string }).delta ?? "";
        appendToCurrentTurn({ input: delta });
        break;
      }
      case "conversation.item.input_audio_transcription.completed": {
        const full = (event as { transcript?: string }).transcript;
        if (typeof full === "string") {
          setCurrentTurn((t) => ({ ...t, input: full }));
        }
        break;
      }
      case "response.output_audio_transcript.delta": {
        const delta = (event as { delta?: string }).delta ?? "";
        appendToCurrentTurn({ output: delta }, /* resolveLatency */ true);
        break;
      }
      case "response.output_audio_transcript.done": {
        const full = (event as { transcript?: string }).transcript;
        setCurrentTurn((t) => ({
          ...t,
          ...(typeof full === "string" ? { output: full } : {}),
          done: true,
        }));
        currentTurnIdRef.current = null;
        break;
      }
      case "error": {
        const err = (event as { error?: { message?: string } }).error;
        setErrorMessage(err?.message ?? "unknown realtime error");
        pushToast(`ERR · ${err?.message ?? "realtime"}`, "error");
        break;
      }
      default:
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ensureTurn = useCallback(() => {
    if (currentTurnIdRef.current) return currentTurnIdRef.current;
    const id = `turn-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    currentTurnIdRef.current = id;
    setTurns((prev) =>
      [
        ...prev,
        {
          id,
          target: targetRef.current,
          input: "",
          output: "",
          latencySeconds: null,
          done: false,
        } satisfies TsuyakuTurn,
      ].slice(-MAX_TURNS_DISPLAYED),
    );
    return id;
  }, []);

  const setCurrentTurn = useCallback(
    (updater: (t: TsuyakuTurn) => TsuyakuTurn) => {
      const id = ensureTurn();
      setTurns((prev) =>
        prev.map((t) => (t.id === id ? updater(t) : t)),
      );
    },
    [ensureTurn],
  );

  const appendToCurrentTurn = useCallback(
    (
      patch: Partial<Pick<TsuyakuTurn, "input" | "output">>,
      resolveLatency = false,
    ) => {
      const id = ensureTurn();
      setTurns((prev) =>
        prev.map((t) => {
          if (t.id !== id) return t;
          const next: TsuyakuTurn = { ...t };
          if (typeof patch.input === "string") next.input = t.input + patch.input;
          if (typeof patch.output === "string")
            next.output = t.output + patch.output;
          if (resolveLatency && t.latencySeconds == null) {
            const releaseAt = releaseAtRef.current;
            if (releaseAt != null) {
              next.latencySeconds = (performance.now() - releaseAt) / 1000;
              releaseAtRef.current = null;
            }
          }
          return next;
        }),
      );
    },
    [ensureTurn],
  );

  // ── Lifecycle ─────────────────────────────────────────────
  useEffect(() => {
    return () => {
      managerRef.current?.close();
      managerRef.current = null;
    };
  }, []);

  const handleConnect = useCallback(async () => {
    if (managerRef.current) return;
    setErrorMessage(null);
    const manager = new RTCSessionManager({
      onState: setState,
      onRemoteTrack: (stream) => {
        if (audioElRef.current) {
          audioElRef.current.srcObject = stream;
          // Phase 3: honour the user's output device choice (no-op on iOS
          // Safari — setSinkId is absent there).
          void applySinkId(audioElRef.current, devices?.outputId ?? null);
          void audioElRef.current.play().catch(() => {
            pushToast("tap to enable audio output", "warn");
          });
        }
      },
      onEvent: handleEvent,
      onError: (m) => {
        setErrorMessage(m);
        pushToast(`ERR · ${m}`, "error");
      },
    });
    managerRef.current = manager;
    try {
      await manager.connect(
        settings.tsuyakuOutputLanguage,
        devices?.inputId ?? null,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMessage(msg);
      pushToast(`ERR · ${msg}`, "error");
      manager.close();
      managerRef.current = null;
    }
  }, [
    devices?.inputId,
    devices?.outputId,
    handleEvent,
    pushToast,
    settings.tsuyakuOutputLanguage,
  ]);

  const handleDisconnect = useCallback(() => {
    managerRef.current?.close();
    managerRef.current = null;
    currentTurnIdRef.current = null;
    releaseAtRef.current = null;
  }, []);

  const handlePressStart = useCallback(() => {
    managerRef.current?.startTransmit();
  }, []);

  const handlePressEnd = useCallback(() => {
    managerRef.current?.stopTransmit();
    releaseAtRef.current = performance.now();
  }, []);

  const handleTooShort = useCallback(() => {
    pushToast("ERR · TOO_SHORT · 0x05 — press 0.5s+", "warn");
  }, [pushToast]);

  // ── Render ────────────────────────────────────────────────
  const connected = state === "ready" || state === "holding" || state === "playing";
  const connecting = state === "connecting";
  const disabled = !connected;
  const target = settings.tsuyakuOutputLanguage;
  const targetLabel = TSUYAKU_LANGUAGE_LABELS[target];

  return (
    <section className="flex min-h-full flex-col gap-5 px-5 py-6">
      <ToastStack toasts={toasts} onDismiss={dismissToast} />

      {/* Hidden audio sink for the remote translation stream. */}
      <audio ref={audioElRef} autoPlay playsInline hidden />

      <div className="space-y-1">
        <div className="font-mono text-[10px] tracking-[0.22em] text-subtext">
          TSU · {stateLabel(state)}
        </div>
        <h1 className="koe-hand text-4xl text-ink">
          {connected ? "on air." : "station closed."}
        </h1>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <MonoLabel label="TARGET">
          {target.toUpperCase()} · {targetLabel}
        </MonoLabel>
        <MonoLabel label="VOICE">dynamic</MonoLabel>
      </div>

      <p className="font-mono text-[10px] tracking-[0.18em] text-subtext">
        source language auto-detected · change target in CFG
      </p>

      {errorMessage && (
        <div
          role="alert"
          className="rounded-box border border-rust bg-cream-soft px-3 py-2 font-mono text-[11px] text-ink"
        >
          <span className="text-rust">ERR · </span>
          {errorMessage}
        </div>
      )}

      <div className="koe-rule-dotted" />

      <TurnHistory turns={turns.slice(-3)} />

      <div className="flex flex-1 flex-col items-center justify-end gap-4 pt-4 pb-2">
        <PTTButton
          disabled={disabled}
          onPressStart={handlePressStart}
          onPressEnd={handlePressEnd}
          onTooShort={handleTooShort}
        />
        <div className="flex gap-3">
          {!connected && (
            <button
              type="button"
              onClick={handleConnect}
              disabled={connecting}
              className="rounded-box border border-ink/30 px-4 py-2 font-mono text-[11px] tracking-[0.2em] text-ink hover:bg-ink hover:text-cream disabled:opacity-40"
            >
              {connecting ? "CONNECTING…" : "OPEN CHANNEL"}
            </button>
          )}
          {connected && (
            <button
              type="button"
              onClick={handleDisconnect}
              className="rounded-box border border-ink/30 px-4 py-2 font-mono text-[11px] tracking-[0.2em] text-ink hover:bg-ink hover:text-cream"
            >
              CLOSE CHANNEL
            </button>
          )}
        </div>
        <p className="font-mono text-[10px] tracking-[0.18em] text-subtext">
          hold space · tap & hold on touch
        </p>
      </div>
    </section>
  );
}

function stateLabel(state: TsuyakuState): string {
  switch (state) {
    case "idle":
      return "STBY";
    case "connecting":
      return "LINK…";
    case "ready":
      return "READY";
    case "holding":
      return "TX · HOLD";
    case "playing":
      return "RX · PLAY";
    case "error":
      return "FAULT";
    case "closed":
      return "STBY";
  }
}
